<template>
    <DefaultTemplate :model="model">
        <div class="container">
            <div>
                <Breadcrumbs :breadcrumbs="model.breadcrumbs" className="text-black" />
            </div>
        </div>
        <div class="container">
            <div class="">
                <div>
                    <form @submit.prevent="search()" class="flex mb-4">
                        <input v-model="query" class="w-full flex-1 px-3"/>
                        <button type="submit" class="bg-green-500 py-2 px-4 uppercase hover:bg-blue-200">Søk</button>
                    </form>
                </div>

                <LoadingSpinner v-if="loading" size="2x" />

                <message-box v-if="error && !loading" title="Feil" text="En feil har oppstått i forsøket på å søke. Hvis feilen gjentar seg, vennligst ta kontakt med oss for hjelp til å finne det innholdet du leter etter." type="alert" />
                <message-box v-if="(pageResults.length === 0 && fileResults.length === 0) && query && !loading" title="Ingen treff" text="Vi kan ikke finne noen treff i det du søker etter" type="info" />

                <div v-if="(pageResults.length > 0 || fileResults.length > 0) && !loading" class="md:flex">
                    <!-- Filter -->
                    <div class="w-full px-2 md:w-1/4 md:pt-14 md:pl-4 order-first md:order-last">
                        <h3 class="font-bold text-lg mb-1">
                            Tidsrom
                        </h3>
                        <div class="bg-white px-4 py-2 mb-4">
                            <ul>
                                <li v-for="(timespan, index) in filter.timespan" :key="index">
                                    <label :for="`timespan-${index}`">
                                        <input type="radio" name="timespan" v-model="selectedTimespan" :value="timespan.value" :id="`timespan-${index}`"/>
                                        {{ timespan.title }} ({{ timespan.hits }})
                                    </label>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <!-- // Filter -->

                    <!-- Results -->
                    <div class="w-full px-2 md:px-0 md:w-3/4">
                        <!-- Tab -->
                        <div class="flex">
                            <button
                                @click.prevent="changeTab('pages')"
                                class="py-4 px-10 border hover:text-gray-800 focus:outline-none"
                                :class="[tab === 'pages' ? 'bg-white text-gray-800 border-white' : 'bg-gray-100 text-gray-500 border-gray-200']"
                                title="Bytt til liste over sider"
                                :disabled="pageResults.length === 0"
                            >
                                Sider ({{numHitsPages}})
                            </button>
                            <button
                                @click.prevent="changeTab('files')"
                                class="py-4 px-10 border hover:text-gray-800 focus:outline-none"
                                :class="[tab === 'files' ? 'bg-white text-gray-800 border-white' : 'bg-gray-100 text-gray-500 border-gray-200']"
                                title="Bytt til liste over filer"
                                :disabled="fileResults.length === 0"
                            >
                                Filer ({{numHitsFiles}})
                            </button>
                        </div>
                        <!-- // Tab -->

                        <!-- Pages -->
                        <template v-if="tab === 'pages'">
                            <ol class="divide-y bg-white">
                                <li
                                    v-for="result in pageResults"
                                    :key="result.url"
                                    class="p-4"
                                >
                                    <h3
                                        class="md:flex md:justify-between text-base mb-2 md:items-start"
                                    >
                                        <a
                                            :href="result.url"
                                            :title="result.title"
                                            class="text-blue-500 underline hover:text-red-500"
                                        >
                                            {{ result.title }}
                                        </a>
                                        <span v-if="result.tema" class="flex-none block sm:inline-block sm:justify-self-end bg-green-200 py-1 px-2 text-xs">
                                            {{ result.tema }}
                                        </span>
                                    </h3>
                                    <div v-html="result.extract"></div>
                                </li>
                            </ol>
                            <Pagination v-if="Math.ceil(numHitsPages / pagination.pages.pageSize) > 1" :currentPage="pagination.pages.page" :totalPages="Math.ceil(numHitsPages / pagination.pages.pageSize)" @change="paginatePages"/>
                        </template>
                        <!-- // Pages -->

                        <!-- Files -->
                        <template v-if="tab === 'files'">
                            <ol class="divide-y bg-white">
                                <li
                                    v-for="result in fileResults"
                                    :key="result.url"
                                    class="p-4"
                                >
                                    <h3
                                        class="md:flex md:justify-between text-base mb-2"
                                    >
                                        <a
                                            :href="result.url"
                                            :title="result.title"
                                            class="text-blue-500 underline hover:text-red-500"
                                            taget="_blank"
                                        >
                                            {{ result.title }}
                                        </a>
                                    </h3>
                                </li>
                            </ol>
                            <Pagination v-if="Math.ceil(numHitsFiles / pagination.files.pageSize) > 1" :currentPage="pagination.files.page" :totalPages="Math.ceil(numHitsFiles / pagination.files.pageSize)" @change="paginateFiles"/>
                        </template>
                        <!-- // Files -->
                    </div>
                    <!-- // Results -->
                </div>
            </div>
        </div>
    </DefaultTemplate>
</template>

<script>
import api from '@/Scripts/api/api';
import Breadcrumbs from '@/Scripts/components/organisms/Breadcrumbs/breadcrumbs.vue';
import LoadingSpinner from '@/Scripts/components/atoms/LoadingSpinner/loadingSpinner.vue';
import MessageBox from '../atoms/Messagebox/MessageBox.vue';
import Pagination from '@/Scripts/components/organisms/Pagination/pagination.vue';

export default {
    props: {
        model: Object,
    },
    components: {
        Breadcrumbs,
        LoadingSpinner,
        MessageBox,
        Pagination
    },
    data() {
        return {
            loading: false,
            error: false,
            query: null,
            pageResults: [],
            fileResults: [],
            timespan: 'All',
            tab: 'pages',
            selectedTimespan: null,  // this.setTimespanDate(1),
            totalHits: 0,
            numHitsPages: 0,
            numHitsFiles: 0,
            pagination: {
                pages: {
                    page: 1,
                    pageSize: 20
                },
                files: {
                    page: 1,
                    pageSize: 20
                }
            },
            filter: {
                timespan: [
                    {
                        value: this.setTimespanDate(1),
                        title: 'Siste år',
                        hits: 0
                    },
                    {
                        value: this.setTimespanDate(3),
                        title: 'Siste 3 år',
                        hits: 0
                    },
                    {
                        value: null,
                        title: 'Alle år',
                        hits: 0
                    },
                ],
            }
        };
    },
    methods: {
        search() {
            this.loading = true;
            this.error = false;
            const body = {
                words: this.query,
                searchSite: 'All',
                publishDate: this.selectedTimespan,
                pageNumberPages: this.pagination.pages.page - 1, // In the pagination, the first page must be 1 while in the search it must be 0. Therefore, subtract 1 here to get the correct page in the search
                pageNumberFiles: this.pagination.files.page - 1, // In the pagination, the first page must be 1 while in the search it must be 0. Therefore, subtract 1 here to get the correct page in the search
                pageSize: 20,
                orderByResultDate: true
            };
            api.postSearch(body).then(response => {
                this.loading = false;
                const data = response.data;
                if (!data.error) {
                    this.pageResults = data.Hits;
                    this.fileResults = data.Filehits;
                    this.numHitsFiles = data.NumHitsFiles;
                    this.numHitsPages = data.NumHitsPages;

                    // Set tab to file if pages = 0 and file > 0
                    if (data.Hits.length === 0 && data.Filehits.length > 0) {
                        this.tab = 'files';
                    }

                    // Set timehits on timespan in filters
                    if (data.FacetDate.length > 0) {
                        this.setTimeSpanHits(data.FacetDate);
                    }
                }
            }).catch(error => {
                this.loading = false;
                this.error = true;
            });
        },
        changeTab(tab) {
            this.tab = tab;
        },
        setTimeSpanHits(hits) {
            hits.map((hit, i) => {
                this.filter.timespan[i].hits = hit;
            });
        },
        setTimespanDate(years) {
            const d = new Date();
            const day = ('0' + d.getDate()).slice(-2);
            const month = ('0' + (d.getMonth() + 1)).slice(-2);
            const year = d.getFullYear() - years;
            return year + '-' + month + '-' + day;
        },
        calucalteNumPages() {
            return this.numHitsPages;
        },
        paginatePages(direction) {
            if (direction < this.pagination.pages.page) {
                // Prev
                this.pagination.pages.page--;
            } else {
                // Next
                this.pagination.pages.page++;
            }
            this.search();
        },
        paginateFiles(direction) {
            if (direction < this.pagination.pages.page) {
                // Prev
                this.pagination.pages.page--;
            } else {
                // Next
                this.pagination.pages.page++;
            }
            this.search();
        }
    },
    watch: {
        selectedTimespan: {
            handler() {
                this.search();
            }
        }
    },
    mounted() {
        if (this.$route.query && this.$route.query.query) {
            this.query = this.$route.query.query;
            this.search();
        }
    }
};
</script>
